/**
 * ImagePropControl — Image picker for prop values (URL string).
 *
 * Simplified version of ImageSlotControl that works with plain string URLs
 * instead of SlotValue objects. Used for `image` prop type in widget schemas.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Upload, X } from 'lucide-react'
import { AttachmentPickerDialog } from '@/components/attachments/AttachmentPickerDialog'

interface ImagePropControlProps {
  value: string
  onChange: (v: string) => void
  label: string
}

const UPLOAD_CONTEXT = { doctype: 'File', name: 'upload' } as const

export function ImagePropControl({ value, onChange, label }: ImagePropControlProps) {
  const [url, setUrl] = useState(value ?? '')
  const [pickerOpen, setPickerOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    setUrl(value ?? '')
  }, [value])

  const applyUrl = useCallback(
    (next: string) => {
      setUrl(next)
      clearTimeout(timerRef.current)
      onChange(next)
    },
    [onChange],
  )

  const handleInputChange = (next: string) => {
    setUrl(next)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => applyUrl(next), 300)
  }

  const handleClear = () => applyUrl('')

  useEffect(() => {
    return () => clearTimeout(timerRef.current)
  }, [])

  return (
    <div className="space-y-1.5">
      <Label className="text-[13px] font-medium text-gray-600">{label}</Label>

      {url ? (
        <div className="group relative overflow-hidden rounded-xs border border-gray-200">
          <img
            src={url}
            alt="Preview"
            className="h-24 w-full object-cover"
            onError={(e) => {
              ;(e.target as HTMLImageElement).style.display = 'none'
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-7 gap-1 text-xs"
              onClick={() => setPickerOpen(true)}
            >
              <Upload className="h-3 w-3" />
              Replace
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-7 w-7 p-0"
              onClick={handleClear}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="flex h-24 w-full items-center justify-center gap-2 rounded-xs border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-400 transition-colors hover:border-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <Upload className="h-4 w-4" />
          Upload or choose image
        </button>
      )}

      <Input
        value={url}
        onChange={(e) => handleInputChange(e.target.value)}
        placeholder="Or paste image URL..."
        className="text-xs"
      />

      <AttachmentPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        context={UPLOAD_CONTEXT}
        accept={['image/*']}
        mode="upload-only"
        onCompleted={(result) => {
          const fileUrl = result?.attachments?.[0]?.file_url
          if (fileUrl) {
            applyUrl(fileUrl)
          }
          setPickerOpen(false)
        }}
      />
    </div>
  )
}
